import MaterialIcon from '@components/common/MaterialIcon';
import BadgeT from '@components/tailwind-components/Badge/BadgeT';
import { t } from 'i18next';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type MultiSelectionDropdownProps<T> = {
  selectedValues: T[];
  availableValues: T[];
  valueTranslations: { [key in string]: string };
  placeholder?: string;
  disabled?: boolean;
  // When true, the dropdown stays open after selecting an item (used in immediate selectValue mode)
  keepOpen?: boolean;
  bgBadgeHex?: string;
  maxVisibleBadges?: number;
  // Static max-width applied to each badge. Ignored when autoSizeBadges is true.
  // Accepts a pixel number or any valid CSS value (e.g. 'clamp(40px, 10vw, 120px)').
  maxBadgeWidth?: number | string;
  // When true, badge max-width is computed dynamically from the available button space
  // so badges always fill and truncate correctly regardless of how many are shown.
  autoSizeBadges?: boolean;
  // Immediate mode: called on every item toggle (default behaviour)
  selectValue?: (key: T) => void;
  // Apply-on-close mode: selections are buffered internally and this is called once when the dropdown closes
  onApply?: (values: T[]) => void;
};

export function MultiSelectionDropdown<T>({
  selectedValues,
  selectValue,
  availableValues,
  valueTranslations,
  placeholder,
  disabled = false,
  keepOpen = false,
  bgBadgeHex = '#0554e3',
  maxVisibleBadges = 3,
  // clamp: min 40px on very small screens, scales with viewport, caps at 120px on desktop
  maxBadgeWidth = 'clamp(40px, 15vw, 120px)',
  autoSizeBadges = false,
  onApply,
}: MultiSelectionDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  // Viewport-relative position of the dropdown list, calculated from the trigger button's bounding rect
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  // Computed per-badge max-width derived from the available button width; used when autoSizeBadges is true
  const [computedBadgeWidth, setComputedBadgeWidth] = useState(120);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Internal buffer used only in apply-on-close mode (when onApply is provided)
  const [pendingValues, setPendingValues] = useState<T[]>(selectedValues);

  // Ref so that closeDropdown always reads the latest pendingValues without stale closure
  const pendingValuesRef = useRef(pendingValues);
  pendingValuesRef.current = pendingValues;

  // Ref so that closeDropdown always reads the latest selectedValues without stale closure
  // This is used to skip calling onApply when the user closes without making any changes,
  // which would otherwise trigger a spurious state update in the parent and cause a scroll-to-top.
  const selectedValuesRef = useRef(selectedValues);
  selectedValuesRef.current = selectedValues;

  // Sync internal buffer when the parent resets selectedValues from outside (e.g. view type change)
  useEffect(() => {
    setPendingValues(selectedValues);
  }, [selectedValues]);

  // Values shown in the trigger button and used for checkmark state
  const displayValues = onApply ? pendingValues : selectedValues;

  // Ref keeps the ResizeObserver callback up-to-date without recreating the observer
  const displayValuesLengthRef = useRef(displayValues.length);
  displayValuesLengthRef.current = displayValues.length;

  // Divide the button's usable width equally among the visible badges.
  // button padding: pl-2.5 (10px) + pr-8 (32px) = 42px
  // gap-1 (4px) between each flex child
  // +N overflow badge is ~28px wide when present
  const recalculateBadgeWidth = useCallback(() => {
    if (!buttonRef.current) {
      return;
    }
    const buttonWidth = buttonRef.current.offsetWidth;
    const visibleCount = Math.min(
      displayValuesLengthRef.current,
      maxVisibleBadges,
    );
    if (visibleCount === 0) {
      return;
    }
    const hasOverflow = displayValuesLengthRef.current > maxVisibleBadges;
    const horizontalPadding = 42;
    const gapSize = 4;
    const totalGaps = (visibleCount - 1 + (hasOverflow ? 1 : 0)) * gapSize;
    const overflowBadgeReserve = hasOverflow ? 28 : 0;
    const availableWidth =
      buttonWidth - horizontalPadding - totalGaps - overflowBadgeReserve;
    setComputedBadgeWidth(
      Math.max(40, Math.floor(availableWidth / visibleCount)),
    );
  }, [maxVisibleBadges]);

  // Recompute synchronously before paint when the number of displayed badges changes.
  // useLayoutEffect prevents the oversized badge from flashing on screen before correction.
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useLayoutEffect(() => {
    if (autoSizeBadges) {
      recalculateBadgeWidth();
    }
  }, [displayValues.length, autoSizeBadges, recalculateBadgeWidth]);

  // Recompute whenever the button itself changes size (e.g. container resize)
  useEffect(() => {
    if (!autoSizeBadges || !buttonRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(recalculateBadgeWidth);
    resizeObserver.observe(buttonRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [autoSizeBadges, recalculateBadgeWidth]);

  // When autoSizeBadges is on, use the computed value; otherwise fall back to the static prop
  const effectiveBadgeWidth = autoSizeBadges
    ? computedBadgeWidth
    : maxBadgeWidth;

  // Recalculate the fixed position of the list relative to the trigger button's viewport rect.
  // Must use getBoundingClientRect() (viewport coordinates) to match position: fixed layout.
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) {
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 2,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // After the list renders, check if it overflows the viewport and correct the position.
  // useLayoutEffect runs synchronously after DOM mutation, preventing a visible flash.
  useLayoutEffect(() => {
    if (!isOpen || !listRef.current || !buttonRef.current) {
      return;
    }
    const listRect = listRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let correctedTop = dropdownPosition.top;
    let correctedLeft = dropdownPosition.left;

    // Flip upward if the list bottom overflows the viewport
    if (listRect.bottom > viewportHeight) {
      correctedTop = buttonRect.top - listRect.height - 2;
    }

    // Shift left if the list right edge overflows the viewport
    if (listRect.right > viewportWidth) {
      correctedLeft = buttonRect.right - listRect.width;
    }

    // Clamp to ensure the list never goes off the left or top edge either
    correctedLeft = Math.max(4, correctedLeft);
    correctedTop = Math.max(4, correctedTop);

    if (
      correctedTop !== dropdownPosition.top ||
      correctedLeft !== dropdownPosition.left
    ) {
      setDropdownPosition((previous) => ({
        ...previous,
        top: correctedTop,
        left: correctedLeft,
      }));
    }
  }, [isOpen, dropdownPosition.top, dropdownPosition.left]);

  const closeDropdown = useCallback(() => {
    // * Skip if already closed - avoids spurious onApply calls from the
    // outside-click handler firing when the dropdown was never opened.
    if (!isOpen) {
      return;
    }

    setIsOpen(false);
    if (onApply) {
      const pending = pendingValuesRef.current;
      const current = selectedValuesRef.current;
      // Only notify the parent when the selection actually changed.
      // Closing without changes would otherwise create a new array reference,
      // which triggers unnecessary re-renders and side-effects (e.g. scroll-to-top).
      const hasChanged =
        pending.length !== current.length ||
        pending.some((value) => !current.includes(value));
      if (hasChanged) {
        onApply(pending);
      }
    }
  }, [onApply, isOpen]);

  const handleDropdown = useCallback(() => {
    if (isOpen) {
      closeDropdown();
    } else {
      // Snapshot the button position before opening so the list renders in the right place
      updatePosition();
      setIsOpen(true);
    }
  }, [isOpen, closeDropdown, updatePosition]);

  // Close when the user clicks outside both the trigger button and the portalled list
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const clickedInsideButton = buttonRef.current?.contains(target) ?? false;
      const clickedInsideList = listRef.current?.contains(target) ?? false;
      if (!clickedInsideButton && !clickedInsideList) {
        closeDropdown();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeDropdown]);

  // Keep the portalled list aligned with the trigger button on scroll and resize
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Re-sync position whenever the trigger button changes size (e.g. badges added/removed while open)
  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(buttonRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, updatePosition]);

  const handleItemClick = useCallback(
    (key: T) => {
      if (onApply) {
        // * Buffer the toggle - do not notify the parent until the dropdown closes
        setPendingValues((previous) =>
          previous.includes(key)
            ? previous.filter((value) => value !== key)
            : [...previous, key],
        );
      } else {
        selectValue?.(key);
        if (!keepOpen) {
          setIsOpen(false);
        }
      }
    },
    [onApply, selectValue, keepOpen],
  );

  // Rendered via a portal so it escapes any overflow:hidden / overflow-y:auto ancestor (e.g. modals)
  const dropdownList = isOpen
    ? createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: dropdownPosition.width,
          }}
          className='max-w-[250px] z-[9999] bg-white rounded-md ring-1 ring-gray-200 shadow-sm max-h-[250px] overflow-y-auto'>
          {availableValues.map((key) => {
            const isSelected = displayValues.includes(key);
            return (
              <button
                type='button'
                key={String(key)}
                className='w-full py-1.5 px-2.5 text-left bg-transparent border-0 flex items-center justify-between gap-4 text-gray-600 hover:bg-gray-100 text-xs'
                onClick={() => handleItemClick(key)}>
                <span className='truncate'>
                  {t(valueTranslations[String(key)])}
                </span>
                {isSelected && (
                  <MaterialIcon
                    icon='done'
                    classes={['text-primary-base', 'text-sm', 'shrink-0']}
                  />
                )}
              </button>
            );
          })}
        </div>,
        document.body,
      )
    : null;

  return (
    <div className='relative w-full'>
      <button
        ref={buttonRef}
        type='button'
        disabled={disabled}
        onClick={handleDropdown}
        className='w-full h-auto flex items-center gap-1 pl-2.5 pr-8 py-1 rounded-md ring-1 ring-inset ring-gray-200 focus:ring-1 focus:ring-inset focus:ring-primary-base bg-white cursor-pointer outline-none overflow-hidden disabled:cursor-not-allowed disabled:opacity-50'>
        {displayValues.length > 0 ? (
          <>
            {/* Show up to maxVisibleBadges selected items */}
            {displayValues.slice(0, maxVisibleBadges).map((value) => (
              <BadgeT
                key={String(value)}
                text={t(valueTranslations[String(value)])}
                className='shrink-0 font-thin !text-pico inline-flex leading-none !py-1 rounded-sm'
                style={{ maxWidth: effectiveBadgeWidth }}
                truncate={true}
                bgColorHex={bgBadgeHex}
              />
            ))}
            {/* Overflow counter shown when selected items exceed maxVisibleBadges */}
            {displayValues.length > maxVisibleBadges && (
              <BadgeT
                text={`+${displayValues.length - maxVisibleBadges}`}
                className='shrink-0 font-thin !text-pico inline-flex leading-none !py-1 rounded-sm'
                bgColorHex={bgBadgeHex}
              />
            )}
          </>
        ) : (
          <span className='text-gray-300 text-xs leading-5 select-none'>
            {placeholder ?? t('common.no-selected')}
          </span>
        )}
      </button>

      <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-1.5 text-gray-600'>
        <MaterialIcon icon='keyboard_arrow_down' />
      </div>

      {dropdownList}
    </div>
  );
}

export default MultiSelectionDropdown;
